"use client";

import { useEffect, useRef } from "react";
import { useSession } from "next-auth/react";
import { trackCompleteRegistration } from "@/shared/lib/meta-pixel-client";

/**
 * Fires the browser CompleteRegistration Pixel for GOOGLE signups.
 *
 * The email path fires inline from the register response. Google creates the user
 * during the server-side OAuth callback, so the browser never sees the minted id.
 * Once the authenticated user lands here, we ask the server for a pending
 * registration id (see /api/auth/pending-registration-event) and, if one exists
 * (i.e. this really is a fresh signup, not a returning login), fire the Pixel with
 * that SAME id so Meta deduplicates it against the server-side CAPI event.
 *
 * The endpoint stamps the row on read, so it returns an id at most once per
 * registration — a returning login gets `{}` and nothing fires.
 */
export default function MetaPixelRegistrationBridge() {
  const { status } = useSession();
  const checkedRef = useRef(false);

  useEffect(() => {
    if (status !== "authenticated" || checkedRef.current) return;
    checkedRef.current = true;

    (async () => {
      try {
        const res = await fetch("/api/auth/pending-registration-event");
        if (!res.ok) return;
        const data = (await res.json()) as { eventId?: string };
        if (data.eventId) trackCompleteRegistration(data.eventId);
      } catch {
        /* best-effort — never disrupt the app */
      }
    })();
  }, [status]);

  return null;
}
